import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCalendarAlt, faCheckCircle, faEnvelope, faGlobe, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { faFacebookSquare, faTwitterSquare, faLinkedin } from '@fortawesome/fontawesome-free-brands';

// Mixins / Modules
import { deparam } from '../../../../../../modules/global';
import isResizable from '../../../../../mixins/resizable';

// Models
import Language from '../../../../../../models/v2/language';
import Persona from '../../../../../../models/v2/persona';

// Base
import Avatar from '../../../../base/avatar';
import Badge from '../../../../base/badge';
import Button from '../../../../base/button';
import Col from '../../../../base/col';
import EllipsisExpand from '../../../../base/ellipsis_expand';
import Row from '../../../../base/row';
import Separator from '../../../../base/separator';
import SlideDown from '../../../../base/slide_down';

// Related Components
import ProfileShowLoading from './show/loading';

class Show extends React.Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    user: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    const params = deparam(location.search.slice(1));
    this.state = {
      currentPersonaId: params.persona_id,
      loading: true,
      currentPersona: {},
      languages: [],
    }
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const { profile } = this.props;
    const { currentPersonaId } = this.state;
    let languagePromise = new Promise((resolve, _reject) => { resolve([])});
    if (profile.language_ids.length > 0) {
      languagePromise = Language.where({ id_in: profile.language_ids }).all(false);
    }
    if (currentPersonaId) {
      Promise.all([
        Persona.find(currentPersonaId),
        languagePromise,
      ])
        .then((values) => {
          const currentPersona = values[0] || {};
          const languages = values[1] || [];

          if (currentPersona) {
            this.setState({
              currentPersona,
              languages,
              loading: false,
            })
          } else {
            this.setState({ loading: false });
          }
        })
        .catch((error) => Window.flash.error(error));
    } else {
      this.setState({ loading: false });
    }
  }

  render() {
    const { isMobile, user, profile } = this.props;
    const { loading, currentPersona, languages } = this.state;

    if (loading) {
      return <ProfileShowLoading />
    }

    return (
      <div className={classNames('flex', {
        'flex-col': isMobile,
      })}>
        <div className='margin-right-20'>
          <Avatar {...{
            key: profile.id,
            src: profile.avatar_url_thumb,
            letterText: profile.full_name,
            size: 'lg',
          }} />
        </div>
        <div className='ellipsis flex-grow-1'>
          <Row noMargin>
            <Col s={12} className='lg-font'>
              <div className='flex'>
                <div className={classNames({ 'flex-grow-1 vertical-align': !isMobile })}>
                  <EllipsisExpand>
                    {profile.full_name}
                  </EllipsisExpand>
                </div>
              </div>
            </Col>
            <Col s={12} className='primary-color sm-font margin-bottom-10 margin-top-7'>
              { currentPersona.name }&nbsp;
            </Col>
            <Col s={12} className='xxsm-font margin-bottom-5'>
              <ul className='list-style-none margin-0 padding-0 flex flex-wrap'>
                {
                  profile.location &&
                  <li className='margin-right-15 margin-bottom-5'>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className='margin-right-5 gray-900' />
                    {profile.location}
                  </li>
                }
                {
                  languages.length > 0 &&
                  <li className='margin-right-15 margin-bottom-5'>
                    <FontAwesomeIcon icon={faGlobe} className='margin-right-5 gray-900' />
                      {languages.map(({ name }) => name).join(', ')}
                  </li>
                }
              </ul>
            </Col>
            {
              profile.markdown_description &&
              <Col s={12}>
                <SlideDown minHeight={40} className='white-space-normal'>
                  <div
                    className='xxsm-font wysiwyg-content'
                    dangerouslySetInnerHTML={{ __html: profile.markdown_description }}
                  />
                </SlideDown>
              </Col>
            }
            <Col s={12}>
              <Separator className='margin-top-10 margin-bottom-10' />
            </Col>
            <Col s={12}>
              <div className='flex flex-row justify-space-between'>
                <div className='xxsm-font vertical-align flex-wrap'>
                  {
                    profile.website_url &&
                    <div className='flex flex-row'>
                      <FontAwesomeIcon
                        icon={faGlobe}
                        className='margin-right-5 gray-900'
                        fixedWidth
                      />
                      <EllipsisExpand>
                        <a href={profile.website_url} className='primary-color' target='_blank'>{profile.website_url}</a>
                      </EllipsisExpand>
                    </div>
                  }
                </div>
                <div className='vertical-align'>
                  <div className='flex center-align xxsm-font flex-wrap'>
                    {
                      profile.facebook &&
                      <Button size='custom' to={profile.facebook} target='_blank' className='primary-color'>
                        <FontAwesomeIcon icon={faFacebookSquare} className='margin-right-10 width-15' size='2x' fixedWidth />
                      </Button>
                    }
                    {
                      profile.twitter &&
                      <Button size='custom' to={profile.twitter} target='_blank' className='primary-color'>
                        <FontAwesomeIcon icon={faTwitterSquare} className='margin-right-10 width-15' size='2x' fixedWidth />
                      </Button>
                    }
                    {
                      profile.linkedin &&
                      <Button size='custom' to={profile.linkedin} target='_blank' className='primary-color'>
                        <FontAwesomeIcon icon={faLinkedin} className='margin-right-10 width-15' size='2x' fixedWidth />
                      </Button>
                    }
                  </div>
                </div>
                <div className='flex flex-wrap vertical-align justify-space-between'>
                  <div className='gray-900 xxsm-font vertical-align min-width-200 justify-flex-end'>
                    Member Since: <span className='dark-primary-color margin-left-10'>{moment(user.created_at).format('MMM Do, YYYY')}</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }
}

export default isResizable(Show);
